<template>
  <div class="doc-row" :class="{ selected: isSelected }">
    <button
      type="button"
      class="doc-check"
      :aria-label="isSelected ? `ยกเลิกเลือก ${label}` : `เลือก ${label}`"
      :aria-pressed="isSelected ? 'true' : 'false'"
      @click="$emit('select', item)"
    >
      <i class="fas fa-check" aria-hidden="true"></i>
    </button>

    <button type="button" class="doc-main" :aria-label="`เปิด ${label}`" @click="$emit('preview', item)">
      <span class="doc-icon" :class="isPdf ? 'is-pdf' : 'is-image'">{{ extLabel }}</span>
      <span class="doc-text">
        <span class="doc-remark">{{ label }}</span>
        <span class="doc-filename">{{ fileName }}</span>
        <span class="doc-meta-mobile">
          {{ treatedText }}
          <template v-if="otherBranch"> · <span class="doc-branch other">{{ branchName }}</span></template>
        </span>
      </span>
    </button>

    <span class="doc-cell" :class="{ muted: !treatment }">{{ treatedText }}</span>
    <span class="doc-cell muted">{{ uploadedText }}</span>
    <span class="doc-cell">
      <span v-if="otherBranch" class="doc-branch other">{{ branchName }}</span>
      <span v-else class="muted">สาขานี้</span>
    </span>

    <div class="doc-actions">
      <b-spinner v-if="item.isDeleting" small variant="danger" label="กำลังลบ" />
      <template v-else>
        <b-button
          v-if="isPdf"
          size="sm"
          variant="outline-secondary"
          class="doc-sign"
          :class="{ 'is-locked': !canGenerateDocument }"
          @click="handleSign"
        >
          <i class="fas fa-signature" aria-hidden="true"></i> เซ็น
          <UpgradeBadge v-if="!canGenerateDocument" class="ml-1" />
        </b-button>
        <button type="button" class="doc-icon-btn doc-download" aria-label="ดาวน์โหลด" @click="$emit('download', item)">
          <i class="fas fa-download" aria-hidden="true"></i>
        </button>
        <b-dropdown size="sm" variant="link" no-caret right toggle-class="doc-icon-btn">
          <template #button-content>
            <i class="fas fa-ellipsis-v" aria-hidden="true"></i>
            <span class="sr-only">ตัวเลือกเพิ่มเติม</span>
          </template>
          <b-dropdown-item @click="$emit('preview', item)">
            <i class="far fa-eye mr-2"></i> เปิดดู
          </b-dropdown-item>
          <b-dropdown-item @click="$emit('download', item)">
            <i class="fas fa-download mr-2"></i> ดาวน์โหลด
          </b-dropdown-item>
          <b-dropdown-divider></b-dropdown-divider>
          <b-dropdown-item variant="danger" @click="$emit('delete', item)">
            <i class="fas fa-trash-alt mr-2"></i> ลบ
          </b-dropdown-item>
        </b-dropdown>
      </template>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import formatDate from "@/utils/formatDate";
import CrossBranchMixin from "@/mixins/CrossBranchMixin";
import UpgradeNoticeMixin from "@/mixins/UpgradeNoticeMixin";
import UpgradeBadge from "@/components/badge/UpgradeBadge.vue";

/** One patient document in the เอกสาร list. On phones it folds to two lines. */
export default {
  name: "DocumentRow",
  components: { UpgradeBadge },
  mixins: [CrossBranchMixin, UpgradeNoticeMixin],
  props: {
    item: {
      type: Object,
      required: true,
    },
    isSelected: {
      type: Boolean,
      default: false,
    },
    // the name the file gets when downloaded
    fileName: {
      type: String,
      default: "",
    },
  },
  computed: {
    ...mapGetters({
      getTreatmentHistoryList: "moduleTreatment/getTreatmentHistoryList",
      canGenerateDocument: "moduleBranch/canGenerateDocument",
    }),
    isPdf() {
      return this.item.fileType === "application/pdf";
    },
    extLabel() {
      if (this.isPdf) return "PDF";
      if (this.item.fileType === "image/png") return "PNG";
      return "JPG";
    },
    label() {
      return this.item.remark || "เอกสาร";
    },
    treatment() {
      return (
        (this.item.treatmentId &&
          (this.getTreatmentHistoryList || []).find(t => t.id === this.item.treatmentId)) ||
        null
      );
    },
    treatedText() {
      return this.treatment ? formatDate(this.treatment.creationDt) : "ไม่ระบุวันที่รักษา";
    },
    uploadedText() {
      return formatDate(this.item.creationDt);
    },
    otherBranch() {
      return this.isOtherBranch(this.item.branchId);
    },
    branchName() {
      return (this.item.branch && this.item.branch.name) || "สาขาอื่น";
    },
  },
  methods: {
    handleSign() {
      if (!this.canGenerateDocument) {
        this.showUpgradeNotice("การเซ็นเอกสารเก็บเข้าแฟ้มคนไข้");
        return;
      }
      this.$emit("sign", this.item);
    },
  },
};
</script>

<style scoped>
.doc-row {
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr) 140px 120px 120px auto;
  align-items: center;
  gap: 0.5rem;
  min-height: 66px;
  padding: 0.25rem 1rem;
  border-bottom: 1px solid #f0f2f5;
}
.doc-row:hover {
  background: #fafbfc;
}
.doc-row.selected {
  background: #f2f7fe;
}
.doc-check {
  width: 22px;
  height: 22px;
  padding: 0;
  border: 2px solid #c3c9d4;
  border-radius: 7px;
  background: #fff;
  color: transparent;
  font-size: 0.6875rem;
  display: flex;
  align-items: center;
  justify-content: center;
}
.doc-row.selected .doc-check {
  border-color: var(--primary, #0b63c5);
  background: var(--primary, #0b63c5);
  color: #fff;
}
.doc-main {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
  padding: 0.375rem 0;
  border: 0;
  background: transparent;
  text-align: left;
  color: inherit;
}
.doc-icon {
  flex-shrink: 0;
  width: 38px;
  height: 46px;
  border-radius: 9px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding-bottom: 5px;
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.04em;
}
.doc-icon.is-pdf {
  background: #fdecea;
  color: #b42318;
}
.doc-icon.is-image {
  background: #e8f0fc;
  color: #1f4fa3;
}
.doc-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.doc-remark {
  font-size: 0.875rem;
  font-weight: 500;
  color: #101828;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.doc-filename {
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  font-size: 0.6875rem;
  color: #5d6679;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.doc-meta-mobile {
  display: none;
  font-size: 0.75rem;
  color: #5d6679;
}
.doc-cell {
  font-size: 0.8125rem;
  color: #101828;
}
.muted {
  color: #5d6679;
}
.doc-branch.other {
  display: inline-block;
  padding: 0.125rem 0.625rem;
  border-radius: 999px;
  background: #f1ebfe;
  color: #5b21b6;
  font-size: 0.75rem;
  font-weight: 600;
}
.doc-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.25rem;
}
.doc-sign {
  min-height: 34px;
  padding: 0 0.875rem;
  border-radius: 999px;
}
.doc-sign.is-locked {
  color: #5d6679;
  border-color: #e8eaef;
}
.doc-icon-btn,
.doc-actions ::v-deep .doc-icon-btn {
  width: 34px;
  height: 34px;
  padding: 0;
  border: 0;
  border-radius: 17px;
  background: transparent;
  color: #3d4557;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.doc-icon-btn:hover,
.doc-actions ::v-deep .doc-icon-btn:hover {
  background: #eef1f5;
}

@media (max-width: 767.98px) {
  .doc-row {
    grid-template-columns: 32px minmax(0, 1fr) auto;
    padding: 0.25rem 0.5rem;
  }
  .doc-cell,
  .doc-filename,
  .doc-download {
    display: none;
  }
  .doc-meta-mobile {
    display: block;
  }
}
</style>
